import { forwardRef } from "react"
import { alpha, Box, badgeClasses, styled } from "@mui/material"
import {
  SelectComponentBase,
  SelectComponentProps
} from "../../ui/inputs/SelectComponent"
import { TooltipFeedbackProps } from "../../feedback/TooltipFeedback"
import { TooltipFeedbackGroupProps } from "../../feedback/TooltipFeedbackGroup"
import { Variant } from "../../types"

// Otherwise there are problems with styled engine.
export const StyledReviewBadge = styled(Box)<
  TooltipFeedbackProps | TooltipFeedbackGroupProps
>(({ theme }) => ({
  margin: theme.spacing(1, 1),

  [theme.breakpoints.down("mobile")]: {
    margin: theme.spacing(0.5, 1)
  },

  [`& ${StyledSelect}`]: {
    margin: 0
  },

  [`& .${badgeClasses.anchorOriginTopLeftRectangular}`]: {
    [theme.breakpoints.down("mobile")]: {
      left: "3px",
      top: "3px"
    }
  }
}))

interface StyledSelectProps extends SelectComponentProps {
  isLesson?: boolean
  badgeVariant?: Variant | null
  questionCompleted?: boolean
}

// Otherwise there are problems with styled engine.
export const StyledSelect = styled(
  forwardRef<unknown, StyledSelectProps>((props, ref) => {
    const { ...rest } = props
    return <SelectComponentBase ref={ref} {...rest} />
  })
)<StyledSelectProps>(
  ({ theme, isLesson, badgeVariant, questionCompleted }) => `
    min-width: 150px;
    background: ${
      isLesson && badgeVariant === Variant.correct
        ? theme.palette.lightGreen[50]
        : isLesson && badgeVariant === Variant.invalid
        ? theme.palette.orange[50]
        : isLesson && badgeVariant === Variant.neutral
        ? theme.palette.blue["A900"]
        : "transparent"
    };

    & svg {
      transform: none !important;
      color: ${theme.palette.additionalColors["bg_500"]};
    }

    & .MuiSelect-nativeInput {
      visibility: hidden;
    }
    & .MuiSelect-select:after {
      content: '';
      width: 0.5rem;
    }
    & .MuiInputBase-input {
      color: ${theme.palette.blueGrey[800]};
      font-weight: ${500};
      ${
        isLesson && questionCompleted
          ? `-webkit-text-fill-color: ${"inherit"};`
          : ""
      }
      display: flex;
      align-items: center;
    }

    & fieldset {
      transition: ${theme.transitions.create(["border-color"])};
      border-width: ${isLesson && badgeVariant ? "2px" : "1px"} !important;
      border-color: ${
        isLesson && badgeVariant === Variant.correct
          ? theme.palette.lightGreen[700]
          : isLesson && badgeVariant === Variant.invalid
          ? theme.palette.orange[700]
          : isLesson && badgeVariant === Variant.neutral
          ? theme.palette.blue["A800"]
          : theme.palette.blueGrey[700]
      } !important;
    }

    &:hover {
      background-color: ${
        isLesson && badgeVariant === Variant.correct
          ? theme.palette.lightGreen[100]
          : isLesson && badgeVariant === Variant.invalid
          ? theme.palette.orange[100]
          : isLesson && badgeVariant === Variant.neutral
          ? theme.palette.blue["A850"]
          : isLesson && questionCompleted
          ? "none"
          : theme.palette.blue[50]
      };
      & fieldset {
        border-width: 2px !important;
        border-color: ${theme.palette.blue[700]} !important;
      }
    }

    & .MuiPopover-paper {
      margin-top: 3px;
    }

    &.Mui-focused {
      background-color: ${theme.palette.blue[50]};
      outline: 3px solid ${alpha(theme.palette.blue[200], 0.2)};
      & fieldset {
        border-width: 2px !important;
        border-color: #2962FF !important;
      }
    }

    & .MuiMenu-list {
      padding-top: ${theme.spacing(1)};
      padding-bottom: ${theme.spacing(1)};
      box-shadow: ${theme.shadows[6]};
    }

    ${[theme.breakpoints.down("mobile")]} {
      height: 30px;
    }
  `
)
